'use client';

/**
 * Simplified storage solution that only uses IndexedDB directly
 * No localStorage involved, no migrations
 * 
 * FIXED: Properly handles database connection lifecycle and errors
 */

// Global database reference
let dbConnection: IDBDatabase | null = null;
let dbPromise: Promise<IDBDatabase> | null = null;

// Constants
const DB_NAME = 'storybaib-storage';
const STORE_NAME = 'storyData';
const DB_VERSION = 1;

/**
 * Clean up database connection
 */
const cleanupDB = () => {
  if (dbConnection) {
    dbConnection.close();
    dbConnection = null;
    dbPromise = null;
  }
};

/**
 * Initialize database connection with proper error handling
 */
const getDB = async (): Promise<IDBDatabase> => {
  // Return existing connection if valid
  if (dbConnection && dbConnection.objectStoreNames.length > 0) {
    try {
      // Test if connection is still valid
      const testTransaction = dbConnection.transaction([STORE_NAME], 'readonly');
      testTransaction.abort(); // Just testing, don't need to complete
      return dbConnection;
    } catch (e) {
      // Connection is invalid, clean up
      cleanupDB();
    }
  }
  
  // Return existing promise if we're already connecting
  if (dbPromise) {
    return dbPromise;
  }
  
  // Create new connection
  dbPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      return reject(new Error('IndexedDB not supported in this environment'));
    }
    
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => {
      console.error('Could not open IndexedDB:', request.error);
      dbPromise = null;
      reject(new Error('Could not open IndexedDB'));
    };
    
    request.onsuccess = () => {
      dbConnection = request.result;
      
      // Handle database closing events
      dbConnection.onclose = () => {
        console.log('IndexedDB connection closed');
        cleanupDB();
      };
      
      dbConnection.onerror = (event) => {
        console.error('Database error:', event);
      };
      
      dbConnection.onversionchange = () => {
        console.log('Database version change detected, closing connection');
        cleanupDB();
      };
      
      resolve(dbConnection);
    };
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    
    request.onblocked = () => {
      console.warn('Database upgrade blocked by open connections');
      cleanupDB();
    };
  });
  
  return dbPromise;
};

/**
 * Execute a database operation with error handling
 */
const executeDBOperation = async <T>(
  operation: (db: IDBDatabase) => Promise<T>,
  fallback: T
): Promise<T> => {
  try {
    const db = await getDB();
    return await operation(db);
  } catch (error) {
    console.warn('Database operation failed:', error);
    return fallback;
  }
};

/**
 * Save data to IndexedDB with proper error handling
 */
const saveToIndexedDB = async (key: string, data: any): Promise<void> => {
  return executeDBOperation(
    (db) => new Promise<void>((resolve, reject) => {
      try {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        
        const request = store.put({
          key,
          value: data,
          timestamp: Date.now()
        });
        
        request.onsuccess = () => resolve();
        request.onerror = () => {
          console.error('Store put error:', request.error);
          reject(new Error('Failed to save data to IndexedDB'));
        };
        
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => {
          console.error('Transaction error:', transaction.error);
          reject(new Error('Transaction failed'));
        };
        
        transaction.onabort = () => {
          console.warn('Transaction aborted');
          reject(new Error('Transaction aborted'));
        };
      } catch (error) {
        console.error('Error creating transaction:', error);
        reject(error);
      }
    }),
    undefined
  );
};

/**
 * Get data from IndexedDB with proper error handling
 */
const getFromIndexedDB = async <T>(key: string, defaultValue: T): Promise<T> => {
  return executeDBOperation(
    (db) => new Promise<T>((resolve, reject) => {
      try {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        
        const request = store.get(key);
        
        request.onsuccess = () => {
          const result = request.result;
          resolve(result ? result.value : defaultValue);
        };
        
        request.onerror = () => {
          console.error('Store get error:', request.error);
          resolve(defaultValue);
        };
        
        transaction.onerror = () => {
          console.error('Transaction error:', transaction.error);
          resolve(defaultValue);
        };
      } catch (error) {
        console.error('Error creating transaction:', error);
        resolve(defaultValue);
      }
    }),
    defaultValue
  );
};

/**
 * Utility to check if IndexedDB is supported
 */
export const isIndexedDBSupported = (): boolean => {
  return typeof window !== 'undefined' && !!window.indexedDB;
};

/**
 * Simple storage interface that uses only IndexedDB
 * No localStorage fallback
 */
export const simpleStorage = {
  setItem: async (key: string, value: any): Promise<void> => {
    if (!isIndexedDBSupported()) {
      console.warn('IndexedDB not supported, data will not be saved');
      return;
    }
    
    return saveToIndexedDB(key, value);
  },
  
  getItem: async <T>(key: string, defaultValue: T): Promise<T> => {
    if (!isIndexedDBSupported()) {
      console.warn('IndexedDB not supported, returning default value');
      return defaultValue;
    }
    
    return getFromIndexedDB(key, defaultValue);
  },
  
  removeItem: async (key: string): Promise<void> => {
    if (!isIndexedDBSupported()) {
      console.warn('IndexedDB not supported, cannot remove item');
      return;
    }
    
    return executeDBOperation(
      (db) => new Promise<void>((resolve) => {
        try {
          const transaction = db.transaction([STORE_NAME], 'readwrite');
          const store = transaction.objectStore(STORE_NAME);
          
          store.delete(key);
          transaction.oncomplete = () => resolve();
          transaction.onerror = () => {
            console.warn('Error removing item:', transaction.error);
            resolve(); // Resolve anyway
          };
        } catch (error) {
          console.warn(`Error removing ${key} from storage:`, error);
          resolve();
        }
      }),
      undefined
    );
  },
  
  clear: async (): Promise<void> => {
    if (!isIndexedDBSupported()) {
      console.warn('IndexedDB not supported, cannot clear storage');
      return;
    }
    
    return executeDBOperation(
      (db) => new Promise<void>((resolve) => {
        try {
          const transaction = db.transaction([STORE_NAME], 'readwrite');
          const store = transaction.objectStore(STORE_NAME);
          
          store.clear();
          transaction.oncomplete = () => resolve();
          transaction.onerror = () => {
            console.warn('Error clearing storage:', transaction.error);
            resolve(); // Resolve anyway
          };
        } catch (error) {
          console.warn('Error clearing storage:', error);
          resolve();
        }
      }),
      undefined
    );
  }
};

/**
 * Initialization function - simplified to just initialize the DB
 */
export const initStorage = async (): Promise<void> => {
  if (!isIndexedDBSupported()) {
    console.warn('IndexedDB is not supported in this browser');
    return;
  }
  
  try {
    // Just initialize the database connection
    await getDB();
    console.log('Storage initialized successfully');
  } catch (error) {
    console.error('Storage initialization error:', error);
    // Don't throw - storage issues shouldn't break the app
  }
};

/**
 * Clean up on page unload
 */
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    cleanupDB();
  });
  
  // Clean up on visibility change (mobile browsers)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      // Don't close immediately, just mark for cleanup
      setTimeout(() => {
        if (document.hidden && dbConnection) {
          console.log('Cleaning up idle database connection');
          cleanupDB();
        }
      }, 30000); // Clean up after 30 seconds if still hidden
    }
  });
}
